/**
 * Client-side telemetry. Fires structured breadcrumb events into the shared
 * event_logs table via the existing POST /api/event-logs endpoint, tagged
 * with a stable browser-tab session id and the client build version so we
 * can reconstruct what a field user was doing when a bug fires — useful when
 * the bug report itself is just "no save information" with no other context.
 *
 * Design principles:
 *  - Never throws and never blocks. Telemetry failures are swallowed.
 *  - Every event carries session_id + client_version + page + ua.
 *  - The companion server hook in createFeedback snapshots recent events
 *    into the new feedback row's error_details so we don't have to query
 *    twice when triaging.
 */

import { apiClient } from './apiClient';
import { changelog } from './changelog';
import { BUILD_SHORT_SHA } from './buildInfo';

const SESSION_KEY = 'irrig_session_id';

/** Per-tab session id. Persists across reloads (sessionStorage), gone on tab close. */
export function getSessionId(): string {
  if (typeof sessionStorage === 'undefined') return 'no-storage';
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

const CLIENT_VERSION = changelog[0]?.version ?? 'v0.0.0';

export type Severity = 'info' | 'warning' | 'error';

export interface LogEventOpts {
  severity?: Severity;
  metadata?: Record<string, unknown>;
}

/**
 * Fire-and-forget client breadcrumb. Posts to /api/event-logs. Never throws.
 * Skips itself (the apiClient interceptor avoids re-logging telemetry failures
 * to prevent infinite recursion).
 */
export async function logEvent(
  category: string,
  message: string,
  opts: LogEventOpts = {},
): Promise<void> {
  try {
    await apiClient.post('/api/event-logs', {
      severity: opts.severity ?? 'info',
      category,
      message,
      metadata: {
        session_id: getSessionId(),
        client_version: CLIENT_VERSION,
        client_build: BUILD_SHORT_SHA,
        page: typeof location !== 'undefined' ? location.pathname : null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 240) : null,
        ...(opts.metadata ?? {}),
      },
    });
  } catch {
    // Telemetry must never break the calling flow. Silent swallow.
  }
}

/**
 * Install handlers for uncaught JS errors and unhandled promise rejections.
 * Call once at app boot. Safe to call before sign-in.
 */
export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (ev: ErrorEvent) => {
    void logEvent('client_window_error', ev.message || 'window error', {
      severity: 'error',
      metadata: {
        filename: ev.filename,
        lineno: ev.lineno,
        colno: ev.colno,
        stack: (ev.error as Error | undefined)?.stack ?? null,
      },
    });
  });
  window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
    const reason = ev.reason as { message?: unknown; stack?: unknown } | undefined;
    const msg = typeof reason?.message === 'string'
      ? reason.message
      : String(reason ?? 'unhandled rejection');
    void logEvent('client_unhandled_rejection', msg, {
      severity: 'error',
      metadata: { stack: typeof reason?.stack === 'string' ? reason.stack : null },
    });
  });
}
